/**
 * Reports translations
 * Skills gap reports UI text
 */

import { LANGUAGES } from '../config';

export const reportsTranslations = {
  [LANGUAGES.EN]: {
    skillsGapReport: 'Company Skills Gap Report',
    generatingReport: 'Generating Skills Gap Report',
    analyzingData: 'Analyzing employee data and skill gaps...',
    errorTitle: 'Error generating report',
    noDataTitle: 'No data available',
    noDataMessage: 'No employee analysis data found. Please ensure employees have completed their CV and job analyses.',
    preparedBy: 'Prepared by',
    downloadPDF: 'Download PDF Report',
    generating: 'Generating',

    // Key Statistics
    totalEmployeesAnalyzed: 'Total Employees Analyzed',
    averageMatchRate: 'Average Match Rate',
    totalMissingSkills: 'Total Missing Skills',
    trainingRecommendations: 'Training Recommendations',

    // Charts and Data
    skillsByDomain: 'Skills Breakdown by Domain',
    masteredVsMissing: 'Mastered vs Missing Skills (%)',
    mastered: 'Mastered',
    missing: 'Missing',
    skillsProficiencyRadar: 'Skills Proficiency Overview',
    masteredSkillsPercentage: 'Mastered Skills Percentage by Domain',
    topMissingSkills: 'Top Missing Skills',
    percentageEmployeesAffected: 'Percentage of Employees Affected',

    // Tables
    mostCommonGaps: 'Most Common Gaps',
    top10: 'Top 10',
    rank: 'Rank',
    skill: 'Skill',
    employeesAffected: '% Employees Affected',
    requiredLevel: 'Required Level',
    currentLevel: 'Current Level',
    severity: 'Severity',

    // Training Recommendations
    trainingRecommendationsTitle: 'Training Recommendations',
    basedOnSkillGaps: 'Based on identified skill gaps and business priorities',
    gap: 'Gap',
    expectedResults: 'Expected Results',
    timeframe: 'Timeframe',

    // Section Headers
    generalData: '📊 1. General Data',
    keyIndicators: '📈 2. Key Indicators',
    skillDomainBreakdown: '🎯 3. Breakdown by Skill Domain',
    departmentBreakdown: '🏢 5. Breakdown by Department/Service',
    trainingRecommendationsSection: '🎓 8. Training Recommendations - Practical Outcomes',
    conclusionSection: '🎯 9. Conclusion & Next Steps',

    // General Data Statistics
    totalEmployeesInSystem: 'Total employees in system',
    withAnalysisData: 'with analysis data',
    jobDescriptionsIntegrated: 'Job descriptions integrated',
    totalRequiredSkills: 'Total required skills',
    skillsCurrentlyHeld: 'Skills currently held',
    totalMissingSkillsLabel: 'Total missing skills',

    // Key Indicators
    averageJobSkillMatchRate: 'Average Job/Skill Match Rate',
    minMaxMatchRates: 'Min/Max Match Rates',
    avgSkillsMasteredPerEmployee: 'Avg Skills Mastered/Employee',
    avgMissingSkillsPerEmployee: 'Avg Missing Skills/Employee',

    // Domain Analysis
    masteredVsMissingByDomain: 'Mastered vs Missing by Domain',
    domainAnalysisSummary: 'Domain Analysis Summary',
    domain: 'Domain',
    masteredPercent: 'Mastered %',
    missingPercent: 'Missing %',
    criticalSkills: 'Critical Skills',

    // Department Breakdown
    departmentService: 'Department/Service',
    totalUsers: 'Total Users',
    avgMatchRate: 'Avg Match Rate',
    mostCriticalMissingSkill: 'Most Critical Missing Skill',
    totalMissingSkillsColumn: 'Total Missing Skills',
    good: 'Good',
    fair: 'Fair',
    needsAttention: 'Needs Attention',

    // Training Recommendations Section
    practicalOutcomesFormat: 'Format: Practical outcomes → Tasks/activities employees will be able to perform after training',
    rankColumn: 'Rank',
    skillColumn: 'Skill',
    domainColumn: 'Domain',
    gapRequiredVsCurrent: 'Gap (Required vs Current)',
    expectedResultsAfterTraining: 'Expected Results (After Training)',
    priorityColumn: 'Priority',
    timeframeColumn: 'Timeframe',
    ableTo: 'Able to:',
    trainingPrioritySummary: 'Training Priority Summary',
    critical1To3Months: 'Critical (1-3 months)',
    important3To6Months: 'Important (3-6 months)',
    standard6To12Months: 'Standard (6-12 months)',

    // Conclusion
    conclusionNextSteps: 'Conclusion & Next Steps',
    keyStrengthsIdentified: '✅ Key Strengths Identified',
    analysisTopPerforming: 'Analysis of current top-performing skill domains',
    criticalWeaknessesIdentified: '⚠️ Critical Weaknesses Identified',
    noCriticalWeaknessesIdentified: 'No critical weaknesses identified',
    priorityTrainingAreasSection: '🎯 Priority Training Areas',
    noPriorityAreasSpecified: 'No priority areas specified',
    recommendedTimelineSection: '⏰ Recommended Timeline',
    analysisSummarySection: '📈 Analysis Summary',
    domainsAnalyzed: 'Domains Analyzed',
    departmentsCovered: 'Departments Covered',
    criticalRecommendations: 'Critical Recommendations',
    totalRecommendationsLabel: 'Total Recommendations',
    defaultTimeline: '1-3 months for critical gaps, 3-6 months for important skills, 6-12 months for comprehensive upskilling'
  },
  [LANGUAGES.FR]: {
    skillsGapReport: 'Rapport des lacunes de compétences de l\'entreprise',
    generatingReport: 'Génération du rapport des lacunes de compétences',
    analyzingData: 'Analyse des données des employés et des lacunes de compétences...',
    errorTitle: 'Erreur lors de la génération du rapport',
    noDataTitle: 'Aucune donnée disponible',
    noDataMessage: 'Aucune donnée d\'analyse d\'employé trouvée. Veuillez vous assurer que les employés ont terminé leurs analyses de CV et d\'emploi.',
    preparedBy: 'Préparé par',
    downloadPDF: 'Télécharger le rapport PDF',
    generating: 'Génération',

    // Key Statistics
    totalEmployeesAnalyzed: 'Total d\'employés analysés',
    averageMatchRate: 'Taux de correspondance moyen',
    totalMissingSkills: 'Total de compétences manquantes',
    trainingRecommendations: 'Recommandations de formation',

    // Charts and Data
    skillsByDomain: 'Répartition des compétences par domaine',
    masteredVsMissing: 'Compétences maîtrisées vs manquantes (%)',
    mastered: 'Maîtrisées',
    missing: 'Manquantes',
    skillsProficiencyRadar: 'Aperçu de la maîtrise des compétences',
    masteredSkillsPercentage: 'Pourcentage de compétences maîtrisées par domaine',
    topMissingSkills: 'Principales compétences manquantes',
    percentageEmployeesAffected: 'Pourcentage d\'employés affectés',

    // Tables
    mostCommonGaps: 'Lacunes les plus courantes',
    top10: 'Top 10',
    rank: 'Rang',
    skill: 'Compétence',
    employeesAffected: '% d\'employés affectés',
    requiredLevel: 'Niveau requis',
    currentLevel: 'Niveau actuel',
    severity: 'Sévérité',

    // Training Recommendations
    trainingRecommendationsTitle: 'Recommandations de formation',
    basedOnSkillGaps: 'Basées sur les lacunes de compétences identifiées et les priorités commerciales',
    gap: 'Lacune',
    expectedResults: 'Résultats attendus',
    timeframe: 'Délai',

    // Section Headers
    generalData: '📊 1. Données générales',
    keyIndicators: '📈 2. Indicateurs clés',
    skillDomainBreakdown: '🎯 3. Répartition par domaine de compétences',
    departmentBreakdown: '🏢 5. Répartition par département/service',
    trainingRecommendationsSection: '🎓 8. Recommandations de formation - Résultats pratiques',
    conclusionSection: '🎯 9. Conclusion et prochaines étapes',

    // General Data Statistics
    totalEmployeesInSystem: 'Total des employés dans le système',
    withAnalysisData: 'avec données d\'analyse',
    jobDescriptionsIntegrated: 'Descriptions de poste intégrées',
    totalRequiredSkills: 'Total des compétences requises',
    skillsCurrentlyHeld: 'Compétences actuellement détenues',
    totalMissingSkillsLabel: 'Total des compétences manquantes',

    // Key Indicators
    averageJobSkillMatchRate: 'Taux de correspondance emploi/compétence moyen',
    minMaxMatchRates: 'Taux de correspondance Min/Max',
    avgSkillsMasteredPerEmployee: 'Compétences maîtrisées/Employé (moy.)',
    avgMissingSkillsPerEmployee: 'Compétences manquantes/Employé (moy.)',

    // Domain Analysis
    masteredVsMissingByDomain: 'Maîtrisées vs Manquantes par domaine',
    domainAnalysisSummary: 'Résumé de l\'analyse par domaine',
    domain: 'Domaine',
    masteredPercent: '% Maîtrisé',
    missingPercent: '% Manquant',
    criticalSkills: 'Compétences critiques',

    // Department Breakdown
    departmentService: 'Département/Service',
    totalUsers: 'Total d\'utilisateurs',
    avgMatchRate: 'Taux corresp. moy.',
    mostCriticalMissingSkill: 'Compétence manquante la plus critique',
    totalMissingSkillsColumn: 'Total des compétences manquantes',
    good: 'Bon',
    fair: 'Correct',
    needsAttention: 'Nécessite attention',

    // Training Recommendations Section
    practicalOutcomesFormat: 'Format : Résultats pratiques → Tâches/activités que les employés pourront effectuer après formation',
    rankColumn: 'Rang',
    skillColumn: 'Compétence',
    domainColumn: 'Domaine',
    gapRequiredVsCurrent: 'Écart (Requis vs Actuel)',
    expectedResultsAfterTraining: 'Résultats attendus (Après formation)',
    priorityColumn: 'Priorité',
    timeframeColumn: 'Délai',
    ableTo: 'Capable de :',
    trainingPrioritySummary: 'Résumé des priorités de formation',
    critical1To3Months: 'Critique (1-3 mois)',
    important3To6Months: 'Important (3-6 mois)',
    standard6To12Months: 'Standard (6-12 mois)',

    // Conclusion
    conclusionNextSteps: 'Conclusion et prochaines étapes',
    keyStrengthsIdentified: '✅ Forces clés identifiées',
    analysisTopPerforming: 'Analyse des domaines de compétences les plus performants actuels',
    criticalWeaknessesIdentified: '⚠️ Faiblesses critiques identifiées',
    noCriticalWeaknessesIdentified: 'Aucune faiblesse critique identifiée',
    priorityTrainingAreasSection: '🎯 Domaines de formation prioritaires',
    noPriorityAreasSpecified: 'Aucun domaine prioritaire spécifié',
    recommendedTimelineSection: '⏰ Calendrier recommandé',
    analysisSummarySection: '📈 Résumé de l\'analyse',
    domainsAnalyzed: 'Domaines analysés',
    departmentsCovered: 'Départements couverts',
    criticalRecommendations: 'Recommandations critiques',
    totalRecommendationsLabel: 'Total des recommandations',
    defaultTimeline: '1-3 mois pour les lacunes critiques, 3-6 mois pour les compétences importantes, 6-12 mois pour la montée en compétences complète'
  }
};